// Custom ATC Button with Quantity Selector Functionality

class CustomATCManager {
  constructor() {
    this.debounceDelay = 140;
    this.updateTimeouts = new Map();
    this.cartData = null;
    this.lastCartFetchAt = 0;
    this.cartCacheTTL = 250;
    this.cartFetchPromise = null;
    this.pendingRefreshTimeout = null;
    this.lastRenderedCartCount = null;
    this.sectionRefreshScheduled = false;
    this.pendingDrawerOpen = false;
    this.isInitialized = false;
    this.isAPICallInProgress = false; // Global flag to prevent multiple API calls
    this.maxAvailableQty = new Map(); // Store max available qty for each variant
    this.bulkQuantityMap = new Map(); // Store allowed stepped quantities for bulk products

    this.init();
  }
    
  init() {
    if (this.isInitialized) return;

    // Fetch current cart data
    this.getCartData().then(() => {
      this.initializeAllATCButtons();
      this.isInitialized = true;
    });

    // Listen for cart updates from other sources
    document.addEventListener('custom-cart-update', () => {
      this.scheduleCartRefresh();
    });
    
    if (window.PUB_SUB_EVENTS) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // If it's from other sources (not our own updates), refresh UI
        if (event.source !== 'custom-atc' && event.source !== 'custom-atc-qty') {
          this.scheduleCartRefresh();
        }
      });
    }

    // Also listen to specific cart-items changes
    document.addEventListener('cart:updated', (event) => {
      if (event?.detail?.source === 'custom-atc') return;
      this.scheduleCartRefresh();
    });

    // Listen for cart builds (when items removed)
    document.addEventListener('cart:build', (event) => {
      if (event?.detail?.source === 'custom-atc') return;
      this.scheduleCartRefresh();
    });

    window.addEventListener('animeal:cart:changed', (event) => {
      const cartData = event?.detail?.cart;
      if (cartData && Array.isArray(cartData.items)) {
        this.syncUIFromCartData(cartData);
        return;
      }
      this.scheduleCartRefresh();
    });

    window.addEventListener('animeal:cart-drawer:rendered', (event) => {
      const cartData = event?.detail?.cart;
      if (cartData && Array.isArray(cartData.items)) {
        this.syncUIFromCartData(cartData);
        return;
      }
      this.scheduleCartRefresh();
    });
  }

  getCartData(force = false) {
    const now = Date.now();
    if (!force && this.cartData && now - this.lastCartFetchAt < this.cartCacheTTL) {
      return Promise.resolve(this.cartData);
    }

    if (this.cartFetchPromise) {
      return this.cartFetchPromise;
    }

    this.cartFetchPromise = fetch('/cart.json', { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((data) => {
        this.cartData = data;
        this.lastCartFetchAt = Date.now();
        return data;
      })
      .catch((error) => {
        console.error('Error fetching cart data:', error);
      })
      .finally(() => {
        this.cartFetchPromise = null;
      });

    return this.cartFetchPromise;
  }

  scheduleCartRefresh(delay = 80, force = false) {
    if (this.pendingRefreshTimeout) clearTimeout(this.pendingRefreshTimeout);
    this.pendingRefreshTimeout = setTimeout(() => {
      this.pendingRefreshTimeout = null;
      this.refreshCartData(force);
    }, delay);
  }

  refreshCartData(force = false) {
    return this.getCartData(force).then((data) => {
      if (!data) return;
      this.syncUIFromCartData(data);
    });
  }

  syncUIFromCartData(cartData) {
    this.cartData = cartData;
    this.updateCartIconCount();
    this.updateAllATCStates();
  }

  initializeAllATCButtons() {
    // Find all product forms with ATC buttons
    const productForms = document.querySelectorAll('product-form form');
    
    productForms.forEach((form) => {
      this.initializeATCButton(form);
    });
  }

  initializeATCButton(form) {
    const atcButton = form.querySelector('[type="submit"][name="add"]');
    if (!atcButton) return;

    const productForm = form.closest('product-form');
    if (!productForm) return;

    const variantIdInput = form.querySelector('input[name="id"]');
    if (!variantIdInput) return;

    const variantId = parseInt(variantIdInput.value);
    if (!Number.isFinite(variantId)) return;
    const wrapper = atcButton.closest('.quick-add') || atcButton.parentElement;

    // Get max available quantity from form data attribute
    const maxAvailable = parseInt(form.getAttribute('data-max-available')) || 999;
    this.maxAvailableQty.set(variantId, maxAvailable);
    this.bulkQuantityMap.set(variantId, this.getBulkQuantityOptions(form));

    // Create wrapper if needed
    let customWrapper = wrapper.querySelector('.custom-atc-wrapper');
    if (!customWrapper) {
      customWrapper = document.createElement('div');
      customWrapper.className = 'custom-atc-wrapper';
      atcButton.parentElement.insertBefore(customWrapper, atcButton);
      customWrapper.appendChild(atcButton);
    }

    // Remove old classes that might interfere
    atcButton.classList.remove('ATC_button', 'quick-add__submit', 'button', 'button--full-width', 'button--secondary');
    atcButton.classList.add('custom-atc-btn');

    // Create quantity selector
    let qtySelector = wrapper.querySelector('.custom-qty-selector-wrapper');
    if (!qtySelector) {
      qtySelector = this.createQuantitySelector(variantId, form);
      customWrapper.appendChild(qtySelector);
    }

    // Check if product is in cart and set initial state
    this.updateFormATCState(form, variantId);

    // Attach event listeners
    if (atcButton.dataset.customAtcBound !== 'true') {
      atcButton.dataset.customAtcBound = 'true';
      atcButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleATCClick(e, form, customWrapper, variantId);
      });
    }
  }

  createQuantitySelector(variantId, form) {
    const wrapper = document.createElement('div');
    wrapper.className = 'custom-qty-selector-wrapper';
    wrapper.dataset.variantId = String(variantId);

    const minusBtn = document.createElement('button');
    minusBtn.className = 'custom-qty-btn custom-qty-btn-minus';
    minusBtn.type = 'button';
    minusBtn.innerHTML = '-';
    minusBtn.setAttribute('aria-label', 'Decrease quantity');

    const display = document.createElement('div');
    display.className = 'custom-qty-display';
    display.innerHTML = '<span class="qty-value">0</span>';

    const plusBtn = document.createElement('button');
    plusBtn.className = 'custom-qty-btn custom-qty-btn-plus';
    plusBtn.type = 'button';
    plusBtn.innerHTML = '+';
    plusBtn.setAttribute('aria-label', 'Increase quantity');

    // Add max quantity message element
    const maxQtyMsg = document.createElement('div');
    maxQtyMsg.className = 'custom-qty-max-msg hidden';
    maxQtyMsg.innerHTML = '<small>Max available: <span class="max-qty-text">0</span></small>';

    wrapper.appendChild(minusBtn);
    wrapper.appendChild(display);
    wrapper.appendChild(plusBtn);
    wrapper.appendChild(maxQtyMsg);

    minusBtn.addEventListener('click', () => this.handleQuantityChange(-1, form, wrapper, variantId));
    plusBtn.addEventListener('click', () => this.handleQuantityChange(1, form, wrapper, variantId));

    return wrapper;
  }

  handleATCClick(event, form, wrapper, variantId) {
    event.preventDefault();
    const atcBtn = wrapper.querySelector('.custom-atc-btn');
    
    // Prevent multiple API calls
    if (this.isAPICallInProgress) {
      return;
    }

    if (atcBtn.getAttribute('aria-disabled') === 'true') return;

    // Disable all buttons globally
    this.disableAllATCButtons();
    window.dispatchEvent(
      new CustomEvent('animeal:cart:request-start', {
        detail: {
          source: 'custom-atc',
          variantId: Number(variantId),
          quantity: Number(form.querySelector('input[name="quantity"]')?.value || 1),
        },
      })
    );

    // Show loader
    this.showLoader(atcBtn, true);
    atcBtn.setAttribute('aria-disabled', 'true');
    atcBtn.classList.add('loading');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(form);
    const quantity = parseInt(formData.get('quantity')) || 1;
    const maxAvailable = this.maxAvailableQty.get(variantId) || 999;

    // Check if trying to add more than max available
    if (quantity > maxAvailable) {
      this.enableAllATCButtons();
      window.dispatchEvent(
        new CustomEvent('animeal:cart:request-end', {
          detail: {
            source: 'custom-atc',
            variantId: Number(variantId),
          },
        })
      );
      
      // Show error message
      this.showMaxAvailableWarning(wrapper, maxAvailable, variantId);
      return;
    }

    // Add doctor ID if available (for vet-app)
    const savedDoctorId = localStorage.getItem('doctor_id');
    if (savedDoctorId) {
      formData.append('properties[doctorId]', savedDoctorId);
    }
    this.appendSectionsToFormData(formData);

    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          // Error adding to cart
          console.error('Error adding to cart:', response);
          this.showLoader(atcBtn, false);
          atcBtn.setAttribute('aria-disabled', 'false');
          atcBtn.classList.remove('loading');
          this.enableAllATCButtons(); // Re-enable all buttons on error
          window.dispatchEvent(
            new CustomEvent('animeal:cart:request-end', {
              detail: {
                source: 'custom-atc',
                variantId: Number(variantId),
              },
            })
          );
          return;
        }

        // Success: avoid extra cart.json fetch; update local/UI immediately.
        this.applyOptimisticAddToCart(variantId, quantity);
        if (this.cartData) {
          this.syncUIFromCartData(this.cartData);
        } else {
          this.showQuantitySelector(wrapper, quantity, variantId, form);
        }
        this.showLoader(atcBtn, false);

        // Update cart sections from the same response when available (single round-trip path).
        if (response.sections) {
          this.updateCartSectionsFromData(response.sections, true);
        } else {
          this.refreshCartDrawer(true);
          this.scheduleCartRefresh(180, true);
        }

        // Trigger cart update events with our source to avoid redundant refresh
        if (window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'custom-atc',
            cartData: response,
            variantId: variantId
          });
        }

        // Also dispatch custom event
        window.dispatchEvent(new CustomEvent('custom-atc-updated', {
          detail: { variantId: variantId, quantity: quantity }
        }));

        // Play sound if available
        const audioElement = form.closest('product-form')?.querySelector('.custom__animal_sound audio');
        if (audioElement) {
          audioElement.play().catch(() => {});
        }

        // Re-enable all buttons after successful operation
        this.enableAllATCButtons();
        window.dispatchEvent(
          new CustomEvent('animeal:cart:request-end', {
            detail: {
              source: 'custom-atc',
              variantId: Number(variantId),
            },
          })
        );
      })
      .catch((error) => {
        console.error('Error adding to cart:', error);
        this.showLoader(atcBtn, false);
        atcBtn.setAttribute('aria-disabled', 'false');
        atcBtn.classList.remove('loading');
        this.enableAllATCButtons(); // Re-enable all buttons on error
        window.dispatchEvent(
          new CustomEvent('animeal:cart:request-end', {
            detail: {
              source: 'custom-atc',
              variantId: Number(variantId),
            },
          })
        );
      });
  }

  handleQuantityChange(delta, form, wrapper, variantId) {
    const display = wrapper.querySelector('.custom-qty-display .qty-value');
    const maxQtyMsg = wrapper.querySelector('.custom-qty-max-msg');
    const maxQtyText = wrapper.querySelector('.max-qty-text');
    const plusBtn = wrapper.querySelector('.custom-qty-btn-plus');

    const currentQty = parseInt(display.textContent) || 0;
    let newQty = currentQty;

    const maxAvailable = this.maxAvailableQty.get(variantId) || 999;
    const bulkOptions = this.getBulkOptionsForVariant(variantId, form, maxAvailable);

    if (bulkOptions.length) {
      if (delta > 0) {
        const nextBulkQty = bulkOptions.find((qty) => qty > currentQty);
        if (!nextBulkQty) {
          if (plusBtn) plusBtn.disabled = true;
          if (maxQtyMsg && maxQtyText) {
            maxQtyText.textContent = bulkOptions[bulkOptions.length - 1];
            maxQtyMsg.classList.remove('hidden');
            setTimeout(() => {
              maxQtyMsg.classList.add('hidden');
            }, 3000);
          }
          return;
        }
        newQty = nextBulkQty;
      } else if (delta < 0) {
        const lowerOptions = bulkOptions.filter((qty) => qty < currentQty);
        newQty = lowerOptions.length ? lowerOptions[lowerOptions.length - 1] : 0;
      }
    } else {
      newQty = Math.max(0, currentQty + delta);
    }

    // console.log(`[ATC] Quantity changed: ${currentQty} -> ${newQty} for variant ${variantId} (Max: ${maxAvailable})`);

    if (newQty > maxAvailable) {
      // console.log(`[ATC] Quantity ${newQty} exceeds max available ${maxAvailable}`);
      if (maxQtyMsg && maxQtyText) {
        maxQtyText.textContent = maxAvailable;
        maxQtyMsg.classList.remove('hidden');
        setTimeout(() => {
          maxQtyMsg.classList.add('hidden');
        }, 3000);
      }

      newQty = maxAvailable;
      display.textContent = newQty;
      if (plusBtn) plusBtn.disabled = true;
      return;
    }

    if (plusBtn && newQty < maxAvailable) {
      plusBtn.disabled = false;
    }

    if (maxQtyMsg) {
      maxQtyMsg.classList.add('hidden');
    }

    display.textContent = newQty;
    this.debouncedCartUpdate(variantId, newQty, form, wrapper);
  }

  debouncedCartUpdate(variantId, newQty, form, wrapper) {
    // Clear existing timeout for this variant
    if (this.updateTimeouts.has(variantId)) {
      clearTimeout(this.updateTimeouts.get(variantId));
    }

    // Set new timeout
    const timeout = setTimeout(() => {
      this.updateCartQuantity(variantId, newQty, form, wrapper);
      this.updateTimeouts.delete(variantId);
    }, this.debounceDelay);

    this.updateTimeouts.set(variantId, timeout);
  }

  updateCartQuantity(variantId, newQty, form, qtySelector) {
    // Prevent multiple API calls
    if (this.isAPICallInProgress) {
      // console.log('[ATC] API call already in progress, ignoring quantity update');
      return;
    }

    const cartItem = this.getCartItemByVariantId(variantId);
    if (!cartItem) return;

    const lineItemIndex = cartItem.index;
    qtySelector.classList.add('loading');
    
    // Disable ALL buttons globally
    this.disableAllATCButtons();
    window.dispatchEvent(
      new CustomEvent('animeal:cart:request-start', {
        detail: {
          source: 'custom-atc-qty',
          variantId: Number(variantId),
          quantity: Number(newQty),
        },
      })
    );

    this.showLoader(qtySelector, true);

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    const body = JSON.stringify({
      line: lineItemIndex,
      quantity: newQty,
      sections: this.getSectionsToRender()
    });
    config.body = body;

    fetch(`${routes.cart_change_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.errors) {
          console.error('Error updating cart:', response.errors);
          this.showLoader(qtySelector, false);
          qtySelector.classList.remove('loading');
          // Re-enable all buttons on error
          this.enableAllATCButtons();
          this.refreshCartData(true);
          window.dispatchEvent(
            new CustomEvent('animeal:cart:request-end', {
              detail: {
                source: 'custom-atc-qty',
                variantId: Number(variantId),
              },
            })
          );
          return;
        }

        // Update cart data/UI immediately from response.
        this.syncUIFromCartData(response);

        // Hide loader first
        this.showLoader(qtySelector, false);
        qtySelector.classList.remove('loading');

        const shouldOpenDrawer = newQty > 0;
        if (response.sections) {
          this.updateCartSectionsFromData(response.sections, shouldOpenDrawer);
        } else {
          this.refreshCartDrawer(shouldOpenDrawer);
        }

        // Re-enable all buttons after successful update
        this.enableAllATCButtons();
        window.dispatchEvent(
          new CustomEvent('animeal:cart:request-end', {
            detail: {
              source: 'custom-atc-qty',
              variantId: Number(variantId),
            },
          })
        );

        // Trigger cart update events with source tracking
        if (window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'custom-atc-qty',
            cartData: response,
            variantId: variantId,
            quantity: newQty
          });
        }

        // Dispatch custom event for other listeners
        window.dispatchEvent(new CustomEvent('custom-atc-qty-updated', {
          detail: { variantId: variantId, quantity: newQty }
        }));
      })
      .catch((error) => {
        console.error('Error updating cart quantity:', error);
        this.showLoader(qtySelector, false);
        qtySelector.classList.remove('loading');
        // Re-enable all buttons on error
        this.enableAllATCButtons();
        this.refreshCartData(true);
        window.dispatchEvent(
          new CustomEvent('animeal:cart:request-end', {
            detail: {
              source: 'custom-atc-qty',
              variantId: Number(variantId),
            },
          })
        );
      });
  }

  updateCartIconCount() {
    if (!this.cartData || !this.cartData.items) {
      return;
    }

    const totalCount = this.cartData.items.reduce((sum, item) => sum + item.quantity, 0);
    if (this.lastRenderedCartCount === totalCount) return;
    
    const cartIcon = document.getElementById('cart-icon-bubble');
    
    if (cartIcon) {
      let countBubble = cartIcon.querySelector('.cart-count-bubble');
      if (!countBubble) {
        countBubble = document.createElement('div');
        countBubble.className = 'cart-count-bubble';
        cartIcon.appendChild(countBubble);
      }
      
      countBubble.innerHTML = `
        <span aria-hidden="true">${totalCount}</span><span class="visually-hidden">${totalCount} items</span>
      `;
      this.lastRenderedCartCount = totalCount;
    }
  }

  updateAllATCStates() {
    const productForms = document.querySelectorAll('product-form form');
    
    productForms.forEach((form) => {
      const variantIdInput = form.querySelector('input[name="id"]');
      if (!variantIdInput) return;

      const variantId = parseInt(variantIdInput.value);
      if (!Number.isFinite(variantId)) return;
      this.updateFormATCState(form, variantId);
    });
  }

  updateFormATCState(form, variantId) {
    const wrapper = form.parentElement.querySelector('.custom-atc-wrapper');
    if (!wrapper) return;

    const qtySelector = wrapper.querySelector('.custom-qty-selector-wrapper');
    const qtyValueElement = qtySelector?.querySelector('.custom-qty-display .qty-value');
    const cartItem = this.getCartItemByVariantId(variantId);
    const targetQty = cartItem?.quantity || 0;
    const isActive = qtySelector?.classList.contains('active');
    const currentQty = qtyValueElement ? parseInt(qtyValueElement.textContent, 10) || 0 : 0;

    if (targetQty > 0) {
      if (!isActive || currentQty !== targetQty) {
        this.showQuantitySelector(wrapper, targetQty, variantId, form);
      } else {
        this.updateQuantityButtonStates(qtySelector, targetQty, variantId);
      }
    } else if (isActive) {
      this.showATCButton(wrapper);
    }
  }

  showATCButton(wrapper, form = null, variantId = null) {
    const atcBtn = wrapper.querySelector('.custom-atc-btn');
    const qtySelector = wrapper.querySelector('.custom-qty-selector-wrapper');

    if (atcBtn) {
      atcBtn.classList.remove('hidden');
      atcBtn.removeAttribute('aria-disabled');
      atcBtn.classList.remove('loading');
      // Ensure button is enabled
      atcBtn.disabled = false;
      
      // Hide any loader in button
      const btnLoader = atcBtn.querySelector('.loading__spinner');
      if (btnLoader) {
        btnLoader.classList.add('hidden');
      }

    }
    
    if (qtySelector) {
      qtySelector.classList.remove('active');
      // Hide loader in selector
      const selectorLoader = qtySelector.querySelector('.loading__spinner');
      if (selectorLoader) {
        selectorLoader.classList.add('hidden');
      }
      // Reset display value
      const display = qtySelector.querySelector('.custom-qty-display .qty-value');
      if (display) {
        display.textContent = '0';
      }
      // Reset buttons state
      const minusBtn = qtySelector.querySelector('.custom-qty-btn-minus');
      const plusBtn = qtySelector.querySelector('.custom-qty-btn-plus');
      if (minusBtn) minusBtn.disabled = false;
      if (plusBtn) plusBtn.disabled = false;
    }
  }

  showQuantitySelector(wrapper, quantity, variantId, form) {
    const atcBtn = wrapper.querySelector('.custom-atc-btn');
    const qtySelector = wrapper.querySelector('.custom-qty-selector-wrapper');
    const display = qtySelector.querySelector('.custom-qty-display .qty-value');

    display.textContent = quantity;
    qtySelector.classList.add('active');
    atcBtn.classList.add('hidden');

    // Update button states
    this.updateQuantityButtonStates(qtySelector, quantity, variantId);
  }

  updateQuantityButtonStates(qtySelector, quantity, variantId) {
    const minusBtn = qtySelector.querySelector('.custom-qty-btn-minus');
    const plusBtn = qtySelector.querySelector('.custom-qty-btn-plus');
    const maxQtyMsg = qtySelector.querySelector('.custom-qty-max-msg');
    const maxQtyText = qtySelector.querySelector('.max-qty-text');

    const maxAvailable = this.maxAvailableQty.get(variantId) || 999;
    const bulkOptions = this.getBulkOptionsForVariant(variantId, null, maxAvailable);

    if (bulkOptions.length) {
      const hasHigherBulkQty = bulkOptions.some((qty) => qty > quantity);

      if (minusBtn) {
        minusBtn.disabled = quantity <= 0;
      }

      if (plusBtn) {
        plusBtn.disabled = !hasHigherBulkQty;
        if (maxQtyMsg && maxQtyText) {
          if (!hasHigherBulkQty && quantity > 0) {
            maxQtyText.textContent = bulkOptions[bulkOptions.length - 1];
            maxQtyMsg.classList.remove('hidden');
          } else {
            maxQtyMsg.classList.add('hidden');
          }
        }
      }
      return;
    }

    if (minusBtn) {
      minusBtn.disabled = quantity <= 0;
    }

    if (plusBtn) {
      if (quantity >= maxAvailable) {
        plusBtn.disabled = true;
        if (maxQtyMsg && maxQtyText) {
          maxQtyText.textContent = maxAvailable;
          maxQtyMsg.classList.remove('hidden');
        }
      } else {
        plusBtn.disabled = false;
        if (maxQtyMsg) {
          maxQtyMsg.classList.add('hidden');
        }
      }
    }
  }

  getBulkQuantityOptions(form) {
    if (!form) return [];

    const inputs = Array.from(form.querySelectorAll('.custom__quantity_sel_cards'));
    if (!inputs.length) return [];

    const values = inputs
      .map((input) => parseInt(input.value, 10))
      .filter((qty) => Number.isFinite(qty) && qty > 0);

    return [...new Set(values)].sort((a, b) => a - b);
  }

  getBulkOptionsForVariant(variantId, form = null, maxAvailable = null) {
    let options = this.bulkQuantityMap.get(variantId) || [];
    if ((!options || !options.length) && form) {
      options = this.getBulkQuantityOptions(form);
      this.bulkQuantityMap.set(variantId, options);
    }

    if (!options.length) return [];
    if (maxAvailable === null || maxAvailable === undefined) return options;

    const capped = options.filter((qty) => qty <= maxAvailable);
    return capped.length ? capped : [];
  }

  getCartItemByVariantId(variantId) {
    if (!this.cartData || !this.cartData.items) return null;

    for (let i = 0; i < this.cartData.items.length; i++) {
      if (this.cartData.items[i].variant_id === variantId) {
        return {
          ...this.cartData.items[i],
          index: i + 1 // Cart API uses 1-based indexing for line items
        };
      }
    }     
    return null;
  }

  getSectionsToRender() {
    return [
      'cart-drawer',
      'cart-icon-bubble',
      'main-cart-items',
      'main-cart-footer'
    ];
  }

  appendSectionsToFormData(formData) {
    if (!formData || formData.has('sections')) return;
    formData.append('sections', this.getSectionsToRender());
    formData.append('sections_url', window.location.pathname);
  }

  applyOptimisticAddToCart(variantId, quantity) {
    const qtyToAdd = Number(quantity) || 0;
    if (qtyToAdd <= 0) return;

    if (!this.cartData || !Array.isArray(this.cartData.items)) {
      this.cartData = { items: [] };
    }

    const existingItem = this.cartData.items.find((item) => item.variant_id === Number(variantId));
    if (existingItem) {
      existingItem.quantity = (Number(existingItem.quantity) || 0) + qtyToAdd;
    } else {
      this.cartData.items.push({
        variant_id: Number(variantId),
        quantity: qtyToAdd,
      });
    }

    // Local optimistic cache is now stale against server totals; allow next sync fetch.
    this.lastCartFetchAt = 0;
  }

  showMaxAvailableWarning(wrapper, maxAvailable, variantId) {
    // console.log(`[ATC] Showing max available warning for variant ${variantId}: ${maxAvailable}`);
    
    // Find or create warning message
    let warningMsg = wrapper.querySelector('.custom-max-qty-warning');
    if (!warningMsg) {
      warningMsg = document.createElement('div');    
      warningMsg.className = 'custom-max-qty-warning';
      wrapper.appendChild(warningMsg);
    }
    
    warningMsg.innerHTML = `<small style="color: #d32f2f; display: block; padding: 8px 0;">Max available: ${maxAvailable}</small>`;
    
    // Auto-hide after 3 seconds
    setTimeout(() => {
      warningMsg.innerHTML = ''; 
    }, 3000);
  }

  disableAllATCButtons() {
    this.isAPICallInProgress = true;

    // Disable all ATC buttons
    const atcButtons = document.querySelectorAll('.custom-atc-btn');
    atcButtons.forEach((btn) => {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
    });

    // Disable all quantity selector buttons (+/-)
    const qtyButtons = document.querySelectorAll('.custom-qty-btn-minus, .custom-qty-btn-plus');
    qtyButtons.forEach((btn) => {
      btn.disabled = true;
    });
  }

  enableAllATCButtons() {
    this.isAPICallInProgress = false;

    // Enable all ATC buttons
    const atcButtons = document.querySelectorAll('.custom-atc-btn');
    atcButtons.forEach((btn) => {
      btn.disabled = false;
      btn.setAttribute('aria-disabled', 'false');
    });

    // Enable all quantity selector buttons (+/-)
    // BUT respect the quantity rules (minus disabled at qty=0)
    const qtyWrappers = document.querySelectorAll('.custom-qty-selector-wrapper.active');
    qtyWrappers.forEach((wrapper) => {
      const minusBtn = wrapper.querySelector('.custom-qty-btn-minus');
      const plusBtn = wrapper.querySelector('.custom-qty-btn-plus');
      const display = wrapper.querySelector('.custom-qty-display .qty-value');
      const variantId = parseInt(wrapper.dataset.variantId, 10);

      if (minusBtn && display) {
        const qty = parseInt(display.textContent) || 0;
        if (Number.isFinite(variantId)) {
          this.updateQuantityButtonStates(wrapper, qty, variantId);
        } else {
          minusBtn.disabled = qty <= 0;
          if (plusBtn) plusBtn.disabled = false;
        }
      }
    });
  }

  showLoader(element, show = true) {
    if (!element) return;
    
    let spinner = element.querySelector('.loading__spinner');
    
    if (show) {
      if (!spinner) {
        spinner = document.createElement('div');
        spinner.className = 'loading__spinner';
        spinner.innerHTML = `
          <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
          </svg>
        `;
        element.appendChild(spinner);
      }
      spinner.classList.remove('hidden');
    } else {
      if (spinner) {
        spinner.classList.add('hidden');  
      }
    }
  }

  refreshCartDrawer(openDrawer = false) {
    this.pendingDrawerOpen = this.pendingDrawerOpen || openDrawer;
    if (this.sectionRefreshScheduled) return;
    this.sectionRefreshScheduled = true;

    const sections = ['cart-icon-bubble', 'cart-drawer', 'main-cart-items', 'main-cart-footer'];
    
    requestAnimationFrame(() => {
      const shouldOpenDrawer = this.pendingDrawerOpen;
      this.pendingDrawerOpen = false;
      this.sectionRefreshScheduled = false;

      try {
        fetch(`/?sections=${sections.join(',')}`, { credentials: 'same-origin' })
          .then(res => res.json())
          .then(data => {
            this.updateCartSectionsFromData(data, shouldOpenDrawer);
          })
          .catch(e => console.error('Error refreshing cart drawer:', e));
      } catch (e) {
        console.error('Error refreshing cart drawer:', e);
      }
    });
  }

  updateCartSectionsFromData(sections, openDrawer = false) {
    if (!sections) return;

    // Update cart icon bubble - ONLY update the count, preserve the rest
    if (sections['cart-icon-bubble']) {
      const icon = document.getElementById('cart-icon-bubble');
      if (icon) {
        // Parse the response to get just the count bubble
        const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newCountBubble = doc.querySelector('.cart-count-bubble');
        
        if (newCountBubble) {
          // Find or create the count bubble in the existing icon
          let countBubble = icon.querySelector('.cart-count-bubble');
          if (!countBubble) {
            countBubble = document.createElement('div');
            countBubble.className = 'cart-count-bubble';
            icon.appendChild(countBubble);
          }
          // Update only the count bubble content
          countBubble.innerHTML = newCountBubble.innerHTML;
        }
      }
    } 

    // Update cart drawer
    if (sections['cart-drawer']) {
      const cartDrawerElement = document.querySelector('cart-drawer');
      const cartDrawerContent = document.querySelector('#CartDrawer');
      if (cartDrawerContent) {
        const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const newDrawerContent = doc.querySelector('#CartDrawer');
        if (newDrawerContent) {
          cartDrawerContent.innerHTML = newDrawerContent.innerHTML;
          if (cartDrawerElement) {
            if (newDrawerContent.querySelector('.cart-drawer__warnings')) {
              cartDrawerElement.classList.add('is-empty');
            } else {
              cartDrawerElement.classList.remove('is-empty');
            }
            const overlay = cartDrawerElement.querySelector('#CartDrawer-Overlay');
            if (overlay) overlay.onclick = () => cartDrawerElement.close();

            // Only open if flag is true
            if (openDrawer) {
              setTimeout(() => cartDrawerElement.open(document.querySelector('.header')), 100);
            }
          }
        }
      }
    }

    // Update main cart items
    if (sections['main-cart-items']) {
      const mainCartItems = document.getElementById('main-cart-items');
      if (mainCartItems) {
        const doc = new DOMParser().parseFromString(sections['main-cart-items'], 'text/html');
        const newCartItems = doc.querySelector('#main-cart-items');
        if (newCartItems) {
          mainCartItems.innerHTML = newCartItems.innerHTML;
        }
      }
    }

    // Update cart footer
    if (sections['main-cart-footer']) {
      const mainCartFooter = document.getElementById('main-cart-footer');
      if (mainCartFooter) {
        const doc = new DOMParser().parseFromString(sections['main-cart-footer'], 'text/html');
        const newCartFooter = doc.querySelector('#main-cart-footer');
        if (newCartFooter) {
          mainCartFooter.innerHTML = newCartFooter.innerHTML;
        }
      }
    }

    // Dispatch events
    const eventDetails = { cart: this.cartData, source: 'custom-atc' };
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: eventDetails }));
    document.dispatchEvent(new CustomEvent('cart:build', { detail: eventDetails }));
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.customATCManager = new CustomATCManager();
  });
} else {   
  window.customATCManager = new CustomATCManager();
}

// Reinitialize when new product forms are dynamically added
if (window.MutationObserver) {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            if (node.tagName === 'PRODUCT-FORM' || node.querySelector?.('product-form')) {
              // Re-run initialization for new product forms
              const forms = node.querySelectorAll?.('product-form form') || [node.querySelector('form')];
              forms.forEach((form) => {
                if (window.customATCManager && form) {
                  window.customATCManager.initializeATCButton(form);
                }
              });
            }
          }
        });
      }
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}


// -------------- Dynamic ATC Disable Based on Inventory -----------------
document.addEventListener('DOMContentLoaded', function () {
  const atcButton = document.querySelector('.custom_product_ATC');
  const productForm = document.querySelector('form[action="/cart/add"]');

  if (!atcButton || !productForm ) return;

  function getMaxInventory() {
    return parseInt(atcButton.dataset.maxQty || "1");
  }

  function getCurrentVariantId() {
    const input = productForm?.querySelector('[name="id"]');
    return input ? parseInt(input.value) : null;
  }

  function getQuantityToAdd() {
    const input = productForm?.querySelector('[name="quantity"]');
    return input ? parseInt(input.value) || 1 : 1;
  }

  async function fetchCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (err) {
      console.error("Cart fetch error:", err);
      return null;
    }
  }

  async function updateButtonState() {
    const variantId = getCurrentVariantId();
    if (!variantId) return;

    const cart = await fetchCart();
    if (!cart) return;

    const item = cart.items.find(i => i.variant_id === variantId);
    const inCartQty = item ? item.quantity : 0;
    const maxInventory = getMaxInventory();

    if (inCartQty >= maxInventory) {
      atcButton.disabled = true;
      atcButton.classList.add('is-disabled');
      atcButton.textContent = 'Sold out';
      atcButton.cursorPointer = 'none';
    } else {
      atcButton.disabled = false;
      atcButton.classList.remove('is-disabled');
      atcButton.textContent = 'Add';
    }
  }

  updateButtonState();

  document.addEventListener('change', function (e) {
    if (e.target.name === 'id' || e.target.name === 'quantity') updateButtonState();
  });

  document.addEventListener('click', function () {
    updateButtonState();
  });
});

